'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ArrowRight, AlertCircle, Radio, RefreshCw, UserX } from 'lucide-react';
import Badge, { dutyStatusVariant } from '@/components/ui/Badge';
import { dutyService, DutyAssignment } from '@/lib/supabase/services/dutyService';
import { useRealtimeDutyToast } from '@/lib/hooks/useRealtimeDutyToast';

function getMinutesUntil(timeStr: string): number | null {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const now = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 60000);
}

function CountdownBadge({ departure }: { departure: string }) {
  const [mins, setMins] = useState<number | null>(null);
  useEffect(() => {
    setMins(getMinutesUntil(departure));
    const t = setInterval(() => setMins(getMinutesUntil(departure)), 60000);
    return () => clearInterval(t);
  }, [departure]);
  if (mins === null || mins < 0 || mins > 90) return null;
  const urgent = mins <= 30;
  return (
    <span className={`ml-1.5 text-[10px] font-700 px-1.5 py-0.5 rounded-md tabular-nums ${urgent ? 'bg-negative/12 text-negative' : 'bg-warning/12 text-warning'}`}>
      {mins}m
    </span>
  );
}

type TabType = 'all' | 'unassigned';

export default function TodayDutyTable() {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [now, setNow] = useState<Date | null>(null);
  const [duties, setDuties] = useState<DutyAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const today = new Date().toISOString().split('T')[0];

  const loadDuties = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    const todayDuties = await dutyService.search({ dutyDate: today });
    setDuties(todayDuties);
    if (!silent) setLoading(false);
    else setRefreshing(false);
  }, [today]);

  useEffect(() => {
    loadDuties();
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [loadDuties]);

  // Real-time subscription — toast + auto-refresh
  useRealtimeDutyToast(() => loadDuties(true));

  const isUnassigned = (duty: DutyAssignment) =>
    duty.driver1Name === '—' || !duty.driver1Id || duty.conductorName === '—' || !duty.conductorId;

  const displayedDuties = activeTab === 'unassigned'
    ? duties.filter(isUnassigned)
    : duties;

  const unassignedCount = duties.filter(isUnassigned).length;

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'all', label: 'All Duties', count: duties.length },
    { id: 'unassigned', label: 'Unassigned', count: unassignedCount },
  ];

  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--glass-border)]">
        <div>
          <h3 className="text-base font-700 text-foreground">Today's Duty Schedule</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {loading ? 'Loading…' : `${displayedDuties.length} ${displayedDuties.length === 1 ? 'duty' : 'duties'} ${activeTab === 'unassigned' ? 'need crew' : 'scheduled'}`}
            {now && !loading && (
              <span className="ml-2 tabular-nums">
                · <span className="text-positive font-600">Live</span> {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
            )}
            {refreshing && (
              <span className="ml-2 inline-flex items-center gap-1 text-primary">
                <RefreshCw size={10} className="animate-spin" /> Updating…
              </span>
            )}
          </p>
        </div>
        <Link href="/duty-assignment" className="flex items-center gap-1.5 text-sm font-600 text-primary hover:text-primary/80 transition-colors duration-150">
          View all <ArrowRight size={14} />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-5 pt-3 pb-0 border-b border-[var(--glass-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-600 rounded-t-lg border-b-2 transition-colors duration-150 -mb-px ${
              activeTab === tab.id
                ? tab.id === 'unassigned' ?'border-negative text-negative bg-negative/5' :'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-black/3 dark:hover:bg-white/3'
            }`}
          >
            {tab.id === 'unassigned' && <UserX size={12} />}
            {tab.label}
            {tab.count !== undefined && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-700 ${
                activeTab === tab.id
                  ? tab.id === 'unassigned' ?'bg-negative/15 text-negative' :'bg-primary/15 text-primary'
                  : tab.id === 'unassigned' && (tab.count ?? 0) > 0
                    ? 'bg-negative/10 text-negative' :'bg-black/8 dark:bg-white/8 text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading today's duties…</div>
        ) : displayedDuties.length === 0 ? (
          <div className="py-12 text-center">
            {activeTab === 'unassigned' ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-positive/10 flex items-center justify-center">
                  <UserX size={20} className="text-positive" />
                </div>
                <p className="text-sm font-600 text-foreground">All duties assigned</p>
                <p className="text-xs text-muted-foreground">Every duty today has a driver and conductor</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No duties scheduled for today</p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--glass-border)] bg-black/3 dark:bg-white/3">
                {['Route', 'Bus', 'Driver', 'Conductor', 'Shift', 'Departure', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedDuties.map((duty) => {
                const isMissing = duty.driver1Name === '—' || duty.conductorName === '—';
                const isActive = duty.status === 'Active';
                return (
                  <tr
                    key={duty.id}
                    onMouseEnter={() => setHoveredRow(duty.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`border-b border-[var(--glass-border)] last:border-0 transition-colors duration-150 ${hoveredRow === duty.id ? 'bg-black/3 dark:bg-white/3' : isMissing ? 'bg-negative/3' : ''}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {isMissing && <AlertCircle size={13} className="text-negative shrink-0" />}
                        {isActive && !isMissing && <Radio size={13} className="text-positive shrink-0 animate-pulse" />}
                        <div>
                          <p className="font-700 text-foreground">{duty.route}</p>
                          <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{duty.routeName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{duty.busNumber}</td>
                    <td className={`px-4 py-3 whitespace-nowrap font-500 ${duty.driver1Name === '—' ? 'text-negative' : 'text-foreground'}`}>{duty.driver1Name}</td>
                    <td className={`px-4 py-3 whitespace-nowrap font-500 ${duty.conductorName === '—' ? 'text-negative' : 'text-foreground'}`}>{duty.conductorName}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">{duty.shift}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="font-mono text-sm font-600 text-foreground tabular-nums">{duty.departureTime}</span>
                        {duty.status === 'Scheduled' && <CountdownBadge departure={duty.departureTime} />}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-positive animate-pulse shrink-0" />}
                        <Badge variant={dutyStatusVariant(duty.status)}>{duty.status}</Badge>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}